import { Component, inject, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators } from '@angular/forms';
import { AccordionComponent, ButtonComponent, ListTableComponent, ColumnType } from '@ppl/ui';
import { AuthorizeDirective } from 'src/app/shared/authorization/authorize.directive.';
import { MEMBER_VIEW_PAGE_IDS, TABS } from 'src/app/shared/constants/page.constants';
import { DialogConfig, DialogService } from 'src/app/shared/services/dialog.service';
import { TimesheetViewComponent } from "../../payroll/timesheets/timesheet-view/timesheet-view.component";
import { TimesheetService } from 'src/app/services/timesheet.service';
import { UserService } from 'src/app/services/user.service';
import { SicktimeService } from 'src/app/services/sicktime.service';
import { ListService } from 'src/app/shared/services/list.service';
import { SickTimeBalance, Employer, UpdateSicktimeCapRequest } from 'src/app/interfaces/sicktime.interface';
import { AddSickTimeComponent } from "../add-sick-time/add-sick-time.component";
import { NgZorroModule } from 'src/app/shared/modules/ng-zorro/ng-zorro.module';
import { STATIC_DATA } from 'src/app/shared/constants/static-data.constants';
import { environment } from 'src/environments/environment';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { LeadDetailsService } from 'src/app/services/lead-details.service';
import { DateTimeFormatterService } from 'src/app/shared/utils/date-time-formatter.service';
import { LookupValueItem } from 'src/app/interfaces/lookup.interface';
import { LookupService } from 'src/app/services/lookup.service';
import { AuthorizationUtility } from 'src/app/shared/authorization/auth.utility';

@Component({
    imports: [
        CommonModule,
        AccordionComponent,
        ButtonComponent,
        AuthorizeDirective,
        AddSickTimeComponent,
        ListTableComponent,
        NgZorroModule,
        ReactiveFormsModule
    ],
    standalone: true,
    selector: 'app-provider-sick-time',
    templateUrl: './provider-sick-time.component.html',
    styleUrls: ['./provider-sick-time.component.css']
})
export class ProviderSickTimeComponent implements OnInit {
    searchForm!: FormGroup;
    pageId: string = '';
    tabId: string = TABS.SickTime;
    sharedDataKey: string = STATIC_DATA.SHARED_PROVIDER_TIMEOFF_DATA;
    actionsToBeHidden: string[] = [];
    @ViewChild('confirmationDialog') confirmationDialog!: TemplateRef<any>;
    @ViewChild('timeoffViewDialog') timeoffViewDialog!: TemplateRef<any>;
    @ViewChild('applySickLeaveDialog') applySickLeaveDialog!: TemplateRef<any>;
    @ViewChild('updateCapDialog') updateCapDialog!: TemplateRef<any>;

    private dialogSvc = inject(DialogService);
    deleteDialogId: any;
    @Input() isReadOnly = false;
    applySickLeaveDialogId: any = null;
    updateCapDialogId: any = null;
    capForm!: FormGroup;
    selectedBalance: SickTimeBalance | null = null;
    allowCapUpdate: boolean = false;
    canApproveDeny: boolean = true;
    isViewTimeOff: boolean = true;
    consumers: any[] = [];
    employers: Employer[] = [];
    balances: SickTimeBalance[] = [];
    loading: boolean = false;
    entry!: string;
    providerId!: number;
    programName: string = '';
    timeOffData: any[] = [];
    lazyLoad: boolean = false;
    // Externally lazy-loaded grids need the server total, otherwise the paginator
    // pins itself to the rows of the current page and never offers page 2.
    timeOffTotalRecords = 0;
    readonly timeOffRecordsPerPage = 20;
    private timeOffFirst = 0;
    statusList: LookupValueItem[] = [];
    processingStatusList: LookupValueItem[] = [];
    providerPplId: string = '';
    ptoDeleteAllowedStatusList: string[] = ['RP', 'PE', 'SB'];
    tableColumns: ColumnType[] = [
        { field: 'year', header: 'Year', width: '15%' },
        { field: 'employerPplId', header: 'Employer PPL ID', width: '15%' },
        { field: 'employerName', header: 'Employer', width: '20%' },
        { field: 'code', header: 'Type', width: '10%' },
        { field: 'annualCap', header: 'Cap', width: '15%' },
        { field: 'accrual', header: 'Accrued (Hrs)', width: '15%' },
        { field: 'used', header: 'Used (Hrs)', width: '15%' },
        { field: 'balance', header: 'Balance (Hrs)', width: '15%' },
        { field: 'balanceToUse', header: 'Available to Use (Hrs)', width: '15%' },
    ];

    // Loaded once from list_provider_timesheet_timeoff_view.json per program; was previously
    // resolved internally by the list-table via [jsonList]. Now the parent owns it.
    timeOffCols: ColumnType[] = [];

    constructor(
        private fb: FormBuilder,
        private timesheetService: TimesheetService,
        private userService: UserService,
        private sicktimeService: SicktimeService,
        private listService: ListService,
        private leadDetailsService: LeadDetailsService,
        private dateTimeFormatter: DateTimeFormatterService,
        private lookupService: LookupService,
        private notificationService: NotificationService,
        private authUtility: AuthorizationUtility
    ) { }

    ngOnInit(): void {
        this.searchForm = this.fb.group({
            dateRange: [null],
            employer: [null]
        });
        this.statusList = this.lookupService.getLookupValue('Business Status');
        this.processingStatusList = this.lookupService.getLookupValue('TS_App_S');
        this.entry = this.userService.getLeadEntry() ?? '';
        const selectedProgram = this.userService.getSelectedProgram();
        this.providerId = JSON.parse(this.userService.getSelectedLead()!);
        this.pageId = MEMBER_VIEW_PAGE_IDS[this.userService.getFirstSegment()!];
        this.programName = selectedProgram?.program_code ?? '';
        this.loadTimeOffCols();
        const userDetails = this.userService.getUserDetails();
        const isFIAdmin = selectedProgram?.roles.some((role: any) => role.role_name === 'FI Admin') && userDetails?.usertype_lkp === 'FI';
        this.canApproveDeny = isFIAdmin ? false : true;
        this.allowCapUpdate = this.authUtility.isButtonVisible(this.pageId, 'AllowSickTimeCapUpdate') && !environment.isExternal;
        this.leadDetailsService.getLeadDetails('provider', this.programName, this.providerId).subscribe(
            (data: any) => {
                if (data.responsedata && data.responsedata.infoData) {
                    this.providerPplId = data.responsedata.infoData?.providerPPLId || '';
                }
            }
        );
        
        if (!userDetails) {
            console.error('no user details found');
            return;
        }

        this.getConsumers();
        this.loadEmployers();
        this.loadBalances();
    }

    private loadTimeOffCols(): void {
        try {
            const config = this.listService.getListConfigData(this.programName, 'list_provider_timesheet_timeoff_view');
            // The JSON uses the legacy shape ({header_id, header_label, jsonpath}). The list-table
            // would normalize these internally only in [jsonList] mode; since we now pass [cols]
            // directly, map to the {field, header} shape it actually reads on render.
            this.timeOffCols = (config?.columns ?? []).map((c: any) => ({
                ...c,
                field: c.field ?? c.jsonpath ?? c.header_id,
                header: c.header ?? c.header_label,
            }));
        } catch (e) {
            console.error('Failed to load time-off list config', e);
            this.timeOffCols = [];
        }
    }

    onLazyLoad(event: any) {
        this.getTimeOffData(event);
    }

    getTimeOffData(lazyEvent?: any) {
        const timeOffPayload = this.buildRequestPayload(
            'staffTimeOffEntry',
            'createdDate'
        );

        if (lazyEvent) {
            const rows = lazyEvent.rows ?? timeOffPayload.recordsPerPage;
            const first = lazyEvent.first ?? 0;
            this.timeOffFirst = first;
            timeOffPayload.recordsPerPage = rows;
            timeOffPayload.startingPageNumber = String(Math.floor(first / rows) + 1);
            if (lazyEvent.sortField) {
                timeOffPayload.orderByColumn = [lazyEvent.sortField];
                timeOffPayload.orderBy = [lazyEvent.sortOrder === -1 ? 'desc' : 'asc'];
            }
        }

        this.listService
            .getListData(timeOffPayload, 'claims')
            .subscribe((data: any) => {
                const list = data.responsedata || [];
                list.forEach((row: any) => {
                    const inTime = this.dateTimeFormatter.formatStringToTime(row.timeIn);
                    const outTime = this.dateTimeFormatter.formatStringToTime(row.timeOut);
                    if (inTime && outTime) {
                        row.billedUnitsInHrs = this.dateTimeFormatter.getDuration(inTime, outTime);
                    } else {
                        row.billedUnitsInHrs = row.billedUnitsInHrs ? this.dateTimeFormatter.formatUnitsToHoursMinutes(row.billedUnitsInHrs * 15) : null;
                    }
                    row.paidUnits = row.paidUnits ? this.dateTimeFormatter.formatUnitsToHoursMinutes(row.paidUnits) : null;

                    if (row.serviceRate != null && !isNaN(Number(row.serviceRate))) {
                        row.serviceRate = Number(row.serviceRate).toFixed(2);
                    }
                    if (row.billableRate != null && !isNaN(Number(row.billableRate))) {
                        row.billableRate = Number(row.billableRate).toFixed(2);
                    }

                    const businessStatus = this.statusList.find((gen: LookupValueItem) => gen.lkpValueCode === row.businessStatusLkp);
                    if (businessStatus) {
                        row.businessStatusText = businessStatus.lkpValueName;
                    }

                    const processingStatus = this.processingStatusList.find((gen: LookupValueItem) => gen.lkpValueCode === row.processingStatusLkp);
                    if (processingStatus) {
                        row.processingStatusText = processingStatus.lkpValueName;
                    }

                    const consumer = this.consumers.find((c: any) => c.consumerId === row.participantId);
                    if (consumer) {
                        row.participantPPLId = consumer.consumerPplId;
                        row.participantName = consumer.consumerFullName;
                    }

                    row.actions = this.setActions(row);
                });
                this.timeOffData = list;
                this.timeOffTotalRecords = this.resolveTimeOffTotal(data, list.length);
                this.lazyLoad = true;
            });
    }

    /**
     * Uses the server total when the response carries one. Without it, assume a
     * full page means more rows exist so the paginator still offers a next page,
     * and settle on the real total once a short or empty page comes back.
     */
    private resolveTimeOffTotal(data: any, pageRowCount: number): number {
        const reported = Number(data?.Count ?? data?.TotalRecords);
        if (Number.isFinite(reported) && reported >= 0) {
            return reported;
        }

        const loadedTotal = this.timeOffFirst + pageRowCount;
        return pageRowCount === this.timeOffRecordsPerPage ? loadedTotal + 1 : loadedTotal;
    }

    handleAddTimeOff(event: any) {
        if (event?.timeOffAdded && this.applySickLeaveDialogId) {
            this.dialogSvc.closeDialog(this.applySickLeaveDialogId);
            this.applySickLeaveDialogId = null;
            this.loadBalances();
        }
    }

    setActions(row: any) {
        if (row.processingStatus) {
            if (this.ptoDeleteAllowedStatusList.includes(row.processingStatus?.lkpValueCode) && row.serviceCode === 'PTO') {
                if (this.canApproveDeny) {
                    row.actions.cancel = true;
                }
            }
        }
        return row.actions || {};
    }


    buildRequestPayload(pageName: string, orderBy: string) {

        const payload: any = {
            pageName: pageName,
            programCode: this.programName,
            condition: 'and',
            recordsPerPage: this.timeOffRecordsPerPage,
            startingPageNumber: '1',
            searchParam: [
                {
                    jsonPath: 'providerId',
                    value: this.providerId,
                    operator: 'Equal',
                },
            ],
            orderByColumn: [],
            orderBy: [],
        };
        return payload;
    }

    viewTimeOff() {
        // Fetch time-off data first
        this.getTimeOffData();

        const dialogConfig: DialogConfig = {
            title: 'View Time Off',
            okText: 'Ok',
            size: 'xx-large',
            position: 'top',
            showClose: true,
            showButtons: false,
        };

        this.dialogSvc.openDialog(
            this.timeoffViewDialog,
            dialogConfig,
            () => this.handleCancel(),
            () => this.handleCancel()
        );
    }

    onAction(event: any): void {
        if (event?.action === 'cancel') {
            this.openConfirmationPopup(event);
        }
    }

    openConfirmationPopup(event: any) {
        const dialogConfig: DialogConfig = {
            title: 'Confirmation',
            okText: 'Yes',
            size: 'medium',
            position: 'top',
            showClose: true,
            showButtons: true,
        };

        this.deleteDialogId = this.dialogSvc.openDialog(
            this.confirmationDialog,
            dialogConfig,
            () => this.deleteTimesheet(event),
            () => this.closeDeleteDialog()
        );
    }

    deleteTimesheet(event: any): void {
        if (event?.data?.timeSheetDetailId) {
            const deleteTimesheetsPayload = { programCode: this.programName, timesheetLineDetailIds: [event.data.timeSheetDetailId] };

            this.timesheetService.deleteTimesheet(deleteTimesheetsPayload)
                .subscribe((response: any) => {
                    if (response.status === 200) {
                        this.getTimeOffData();
                    } else {
                        console.error('Error deleting timesheet:', response);
                    }
                });
        } else {
            console.log(`Timesheet detail id is missing.`);
        }
    }

    closeDeleteDialog() {
        if (this.deleteDialogId) {
            this.dialogSvc.closeDialog(this.deleteDialogId);
            this.deleteDialogId = null;
        }
    }

    applySickLeave() {
        const dialogConfig: DialogConfig = {
            title: 'Apply Time Off',
            okText: 'Ok',
            size: 'medium',
            position: 'top',
            showClose: true,
            showButtons: false,
        };

        this.applySickLeaveDialogId = this.dialogSvc.openDialog(
            this.applySickLeaveDialog,
            dialogConfig,
            () => this.handleCancel(),
            () => this.handleCancel()
        );
    }

    handleCancel() { }

    private getConsumers() {
        this.timesheetService
            .getConsumerList(this.programName, this.providerId)
            .subscribe((data: any) => {
                this.consumers = data.responsedata;
            });
    }

    loadEmployers(): void {
        this.sicktimeService.getEmployers(this.programName, this.providerId.toString()).subscribe({
            next: (response) => {
                this.employers = response.responsedata || [];
                console.log('Employers loaded', this.employers);
            },
            error: (error) => {
                console.error('Error loading employers', error);
                this.employers = [];
            }
        });
    }

    filterBalanceSearch(): void {
        const formValue = this.searchForm.value;
        let fromDate: string | null = null;
        let toDate: string | null = null;
        const employerId = formValue.employer || null;

        if (formValue.dateRange && formValue.dateRange[0] && formValue.dateRange[1]) {
            fromDate = this.formatDate(formValue.dateRange[0]);
            toDate = this.formatDate(formValue.dateRange[1]);
        }

        this.loadBalancesWithFilters(fromDate, toDate, employerId);
    }

    loadBalancesWithFilters(fromDate: string | null, toDate: string | null, employerId: number | null = null): void {
        if (!this.providerId) {
            console.error('Provider ID not available');
            return;
        }

        this.loading = true;
        const request: any = {
            providerId: this.providerId,
            fromDate,
            toDate
        };

        // Add employerId if provided
        if (employerId) {
            request.employerId = employerId;
        }

        // Always use the filtered endpoint for search functionality
        this.sicktimeService.getBalancesByProviderAndEmployer(this.programName, request).subscribe({
            next: (response) => {
                this.balances = response.responsedata || [];
                this.loading = false;
                console.log('Filtered sick time balances loaded', this.balances);
            },
            error: (error) => {
                console.error('Error loading filtered sick time balances', error);
                this.balances = [];
                this.loading = false;
            }
        });
    }

    formatDate(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    updateUsageCap() {
        this.capForm = this.fb.group({
            employer: [null, Validators.required],
            usageCap: [0, [Validators.required, Validators.min(0)]],
            balanceCap: [0, [Validators.required, Validators.min(0)]],
            // Empty by default and mandatory - the user must explicitly choose an effective date.
            // nz-date-picker writes back a Date object; the save handler converts it to a
            // YYYY-MM-DD string at payload time to avoid the toISOString() timezone shift.
            effectiveDate: [null, Validators.required],
        });

        const dialogConfig: DialogConfig = {
            title: 'Update Sick Time Cap',
            size: 'x-large',
            position: 'top',
            showClose: true,
            showButtons: false
        };

        this.updateCapDialogId = this.dialogSvc.openDialog(
            this.updateCapDialog,
            dialogConfig,
            () => { },
            () => { }
        );
    }

    saveCapUpdate(): void {
        if (this.capForm.invalid) {
            // Surface the required-field messages on Save. nzErrorTip only renders once the
            // control is touched AND nz-form-control receives a status emission - markAllAsTouched
            // alone sets the flag but doesn't emit statusChanges, so we also re-run validity.
            Object.values(this.capForm.controls).forEach(control => {
                control.markAsDirty();
                control.markAsTouched();
                control.updateValueAndValidity();
            });
            return;
        }

        const request: UpdateSicktimeCapRequest = {
            programCode: this.programName,
            providerNo: this.providerPplId,
            employerNo: this.capForm.value.employer,
            usageCap: Number(this.capForm.value.usageCap ?? 0),
            balanceCap: Number(this.capForm.value.balanceCap ?? 0),
            // Always send the picker value as a local YYYY-MM-DD string. Date objects get
            // converted via toISOString() during JSON.stringify, which can shift the date by
            // one day across timezones.
            effectiveDate: this.capForm.value.effectiveDate instanceof Date
                ? this.formatDate(this.capForm.value.effectiveDate)
                : this.capForm.value.effectiveDate
        };

        this.sicktimeService.updateSicktimeCap(request).subscribe({
            next: (response) => {
                const errors = response?.responsedata?.errorMessages;
                if (errors && errors.length > 0) {
                    const errorMessage = errors.map((e: string) => {
                        try { return JSON.parse(e)?.title ?? e; } catch { return e; }
                    }).join(' ');
                    this.notificationService.error('Update Failed', errorMessage);
                } else {
                    this.notificationService.success('Success', 'Sick time cap updated successfully.');
                    if (this.updateCapDialogId) {
                        this.dialogSvc.closeDialog(this.updateCapDialogId);
                        this.updateCapDialogId = null;
                    }
                    this.loadBalances();
                }
            },
            // Toast is shown by GlobalErrorHandlerService via the error interceptor.
            // The no-op here absorbs the re-thrown error so it doesn't reach zone-level
            // handling and fire a duplicate toast.
            error: () => {}
        });
    }

    cancelCapUpdate(): void {
        if (this.updateCapDialogId) {
            this.dialogSvc.closeDialog(this.updateCapDialogId);
            this.updateCapDialogId = null;
        }
    }

    loadBalances(): void {
        if (!this.providerId) {
            console.error('Provider ID not available');
            return;
        }

        // Reset the search form
        this.searchForm.reset();

        // Use the unfiltered endpoint for initial load and reset
        this.loading = true;
        this.sicktimeService.getBalancesByProvider(this.programName, {
            providerId: this.providerId,
            fromDate: null,
            toDate: null
        }).subscribe({
            next: (response) => {
                this.balances = response.responsedata || [];
                this.loading = false;
                console.log('Sick time balances loaded (unfiltered)', this.balances);
            },
            error: (error) => {
                console.error('Error loading sick time balances', error);
                this.balances = [];
                this.loading = false;
            }
        });
    }
}
